import { getCGPercentContext, getCGPercentUsers } from "@/graphql/queries";
import { useCallback, useState } from "react";
import useMyActivityStore from "./useMyActivitiesStore";
import { useShowModalResult } from "@/components/modal-result";
import useAuthStore from "@/store/useAuthStore";
import { NextRouter, useRouter } from "next/router";
import {
  CGPContextResponse,
  CGPResponse,
  MyActivityResponse,
  processedActivities,
} from "@/types/my-activities";
import { API, graphqlOperation } from "aws-amplify";
import { capitalize, lowerCase } from "lodash";
import useCGPStore from "./useCGPStore";
import pluralize from "pluralize";
import { userTypeConvert } from "@/utils/helper";
import { formatAWSDateTime } from "@/utils/dateUtils";
import dayjs from "dayjs";
import {
  skillRecommendationsUsecase,
  relationshipUpgradeUsecase,
  connectionRecommendationsUsecase,
  circleEventInviteUsecase,
  circleEventRecommendationUsecase,
  mentorshipRequestUsecase,
  championRequestUsecase,
  skillVerificationRequestUsecase,
  endorsementUsecase,
  connectionActivitiesUsecase,
} from "./activityActions";
import { fetchMyActivitiesHTTP } from "./my-activities/api";

interface ModalResult {
  show: boolean;
  title: string;
  body: string;
}

interface UtilsUsecase {
  successModal: (props: ModalResult) => void;
  errorModal: (props: ModalResult & { type: "error" }) => void;
  refetch: () => Promise<void>;
  router?: NextRouter;
}

const statusAlternative = {
  DECLINED: "DENIED",
  ACCEPTED: "APPROVED",
};

const detectType = (str: string) => {
  if (!str) return "";

  let sample = str;
  sample = sample.replaceAll("SUPER_MENTOR", "Sponsor");
  sample = sample.replaceAll("Super_mentor", "Sponsor");
  sample = sample.replaceAll("super_mentor", "Sponsor");
  sample = sample.replaceAll("Allies", "Connection");
  sample = sample.replaceAll("ALLIES", "Connection");
  sample = sample.replaceAll("MENTEE", "Mentee");
  sample = sample.replaceAll("MENTOR", "Mentor");
  sample = sample.replaceAll("CONNECTION", "Connection");
  return sample;
};

const getTargetType = (notifDDescription: string): string => {
  const strSplit = notifDDescription.split(" ");
  const lastStr = strSplit[strSplit.length - 1];

  if (
    [
      "SUPER_MENTOR",
      "SPONSOR",
      "SPONSOR.",
      "MENTOR",
      "MENTOR.",
      "MENTEE",
      "MENTEE.",
      "ALLY",
      "SPONSOR.",
    ].includes(lastStr)
  ) {
    if (lastStr === "SPONSOR.") {
      return "SPONSOR";
    }
    if (lastStr === "MENTOR.") {
      return "MENTOR";
    }
    if (lastStr === "MENTEE.") {
      return "MENTEE";
    }

    return lastStr;
  }

  return "ALLY";
};

function activitiesProcessor(
  rawActivitiesData: MyActivityResponse
): processedActivities[] {
  const cloneRawActivitiesData = { ...rawActivitiesData };
  return (cloneRawActivitiesData?.orderedItems || []).map((activity) => {
    switch (activity.type) {
      case "ACTIVITIES": {
        const findData =
          cloneRawActivitiesData.activities.find((e) => e.id === activity.id) ||
          null;

        const joinedType = lowerCase(
          findData?.content?.split("joined the ")?.[1] || ""
        );
        const isEvent = findData?.content?.toLowerCase().includes("event");

        return {
          context: findData?.content?.includes(" endorsement")
            ? "empty"
            : "activities",
          createdAt: findData?.createdAt || "",
          description: findData?.content?.includes(' skill:') || findData?.content?.includes(' skill ladder:') ? findData?.content?.replace('You', '<b>You</b>') : `<b>You</b> joined the ${lowerCase(
            findData?.contextPrivacy || ""
          )} ${joinedType}: <b>${findData?.contextName || ""}</b>`,
          contextProfile: {
            id: findData?.contextId || "",
            name: findData?.isAuthorHR ? "Admin" : findData?.contextName || "",
            avatarUrl:
              findData?.contextImage || findData?.attachments?.[0] || "",
            cgPercent: 0,
            type: isEvent ? "EVENT" : "CIRCLE",
            isHR: findData?.isAuthorHR || false,
          },
        } as processedActivities;
      }

      case "CIRCLE_INVITATIONS":
        const findCircleInvitation =
          cloneRawActivitiesData.circleInvitations.find(
            (e) => e.id === activity.id
          ) || null;
        return {
          context: "circleInvitations",
          createdAt: findCircleInvitation?.createdAt || "",
          description: `<span class="capitalize"><b>${findCircleInvitation?.isInviterHR ? "Admin" : findCircleInvitation?.inviter?.firstName || ""} ${findCircleInvitation?.inviter?.lastName || ""}</b></span> invited you to join the ${findCircleInvitation?.circle?.name || "Unknown"} Circle.`,
          contextProfile: {
            id: findCircleInvitation?.inviter?.id || "",
            name: findCircleInvitation?.isInviterHR ? "Admin" :
              `${findCircleInvitation?.inviter?.firstName || ""} ${findCircleInvitation?.inviter?.lastName || ""}` ||
              "",
            avatarUrl: findCircleInvitation?.isInviterHR ? "" : findCircleInvitation?.inviter?.avatarUrl || "",
            cgPercent: 0, // ambil dr store anjenk
            points: findCircleInvitation?.inviter?.points || 1,
            isHR: findCircleInvitation?.isInviterHR || false,
            type: "USER",
          },
          contextReference: {
            id: findCircleInvitation?.circle?.id || "",
            name: findCircleInvitation?.circle?.name || "Unknown",
            avatarUrl: findCircleInvitation?.circle?.imageUrl || "",
            cgPercent: findCircleInvitation?.circle?.CGPercent || 0,
            type: "CIRCLE",
            privacy:
              (findCircleInvitation?.circle?.privacy as never) || "PUBLIC",
          },
          notes:
            findCircleInvitation?.inviterNotes ||
            findCircleInvitation?.requestNotes ||
            "",
          actionPayload: {
            invite: {
              referenceType: "CIRCLE",
              referenceId: findCircleInvitation?.id,
            },
            contextName: findCircleInvitation?.circle?.name || "",
            privacy: findCircleInvitation?.circle?.privacy,
          },
        };

      case "EVENT_INVITATIONS":
        const findEventInvitation =
          cloneRawActivitiesData.eventInvitations.find(
            (e) => e.id === activity.id
          ) || null;
        return {
          context: "eventInvitations",
          createdAt: findEventInvitation?.createdAt || "",
          description: `<span class="capitalize"><b>${findEventInvitation?.isInviterHR ? "Admin" : findEventInvitation?.inviter?.firstName || ""} ${findEventInvitation?.inviter?.lastName || ""}</b></span> invited you to join the ${findEventInvitation?.event?.name || "Unknown"} Event.`,
          contextProfile: {
            id: findEventInvitation?.inviter?.id || "",
            name: findEventInvitation?.isInviterHR ? "Admin" :
              `${findEventInvitation?.inviter?.firstName || ""} ${findEventInvitation?.inviter?.lastName || ""}` ||
              "",
            avatarUrl: findEventInvitation?.isInviterHR ? "" : findEventInvitation?.inviter?.avatarUrl || "",
            cgPercent: 0, // ambil dr store anjenk
            points: findEventInvitation?.inviter?.points || 1,
            isHR: findEventInvitation?.isInviterHR || false,
            type: "USER",
          },
          contextReference: {
            id: findEventInvitation?.event?.id || "",
            name: findEventInvitation?.event?.name || "Unknown",
            avatarUrl: findEventInvitation?.event?.imageUrl || "",
            cgPercent: findEventInvitation?.event?.CGPercent || 0,
            type: "EVENT",
            privacy: (findEventInvitation?.event?.privacy as never) || "PUBLIC",
            isHidden: findEventInvitation?.event?.isHidden,
          },
          notes:
            findEventInvitation?.inviterNotes ||
            findEventInvitation?.requestNotes ||
            "",
          actionPayload: {
            invite: {
              referenceType: "EVENT",
              referenceId: findEventInvitation?.id,
              isHR: findEventInvitation?.isInviterHR || false,
            },
            contextName: findEventInvitation?.event?.name || "",
            privacy: findEventInvitation?.event?.privacy,
          },
        };

      case "CIRCLE_RECOMMENDATIONS":
        const findCircleRecommendation =
          cloneRawActivitiesData.circleRecommendations.find(
            (e) => e.notifId === activity.id
          ) || null;
        return {
          context: "circleRecommendations",
          createdAt: findCircleRecommendation?.createdAt || "",
          description: `<span class="capitalize"><b>${findCircleRecommendation?.isFromHR ? "Admin" : findCircleRecommendation?.userFrom?.firstName || "Unknown User"}</b></span> recommends that you join ${findCircleRecommendation?.contextName || "Unknown"}.`,
          contextProfile: {
            id: findCircleRecommendation?.userFrom?.id || "",
            name: findCircleRecommendation?.isFromHR ? "Admin" :
              findCircleRecommendation?.userFrom?.firstName || "Unknown User",
            avatarUrl: findCircleRecommendation?.isFromHR ? "" : findCircleRecommendation?.userFrom?.avatarUrl || "",
            cgPercent: 0, // ambil dr store anjenk
            points: findCircleRecommendation?.userFrom?.points || 1,
            isHR: findCircleRecommendation?.isFromHR || false,
            type: "USER",
          },
          contextReference: {
            id: findCircleRecommendation?.contextId || "",
            name: findCircleRecommendation?.contextName || "Unknown",
            avatarUrl: findCircleRecommendation?.contextImage || "",
            cgPercent: findCircleRecommendation?.contextCGPercent || 0,
            type: "CIRCLE",
            privacy:
              (findCircleRecommendation?.contextPrivacy as never) || "PUBLIC",
          },
          notes: findCircleRecommendation?.notifMessage || "",
          actionPayload: {
            type: "CIRCLE",
            privacy: findCircleRecommendation?.contextPrivacy,
            id: findCircleRecommendation?.contextId,
            notificationId: findCircleRecommendation?.notifId,
          },
        };

      case "EVENT_RECOMMENDATIONS":
        const findEventRecommendation =
          cloneRawActivitiesData.eventRecommendations.find(
            (e) => e.notifId === activity.id
          ) || null;
        return {
          context: "eventRecommendations",
          createdAt: findEventRecommendation?.createdAt || "",
          description: `<span class="capitalize"><b>${findEventRecommendation?.isFromHR ? "Admin" : findEventRecommendation?.userFrom?.firstName || "Unknown User"}</b></span> recommends that you join ${findEventRecommendation?.contextName || "Unknown"}.`,
          contextProfile: {
            id: findEventRecommendation?.userFrom?.id || "",
            name: findEventRecommendation?.isFromHR ? "Admin" :
              findEventRecommendation?.userFrom?.firstName || "Unknown User",
            avatarUrl: findEventRecommendation?.isFromHR ? "" : findEventRecommendation?.userFrom?.avatarUrl || "",
            cgPercent: 0, // ambil dr store anjenk
            points: findEventRecommendation?.userFrom?.points || 1,
            isHR: findEventRecommendation?.isFromHR || false,
            type: "USER",
          },
          contextReference: {
            id: findEventRecommendation?.contextId || "",
            name: findEventRecommendation?.contextName || "Unknown",
            avatarUrl: findEventRecommendation?.contextImage || "",
            cgPercent: findEventRecommendation?.contextCGPercent || 0,
            type: "EVENT",
            privacy:
              (findEventRecommendation?.contextPrivacy as never) || "PUBLIC",
            isHidden: findEventRecommendation?.contextIsHidden || false,
          },
          notes: findEventRecommendation?.notifMessage || "",
          actionPayload: {
            type: "EVENT",
            privacy: findEventRecommendation?.contextPrivacy,
            id: findEventRecommendation?.contextId,
            notificationId: findEventRecommendation?.notifId,
          },
        };

      case "CONNECTION_RECOMMENDATIONS":
        const findConnectionRecommendation =
          cloneRawActivitiesData.connectionRecommendations.find(
            (e) => e.notifId === activity.id
          ) || null;
        const descRawCon = findConnectionRecommendation?.notifDescription || "";
        const userFromName = `${findConnectionRecommendation?.userFrom?.firstName} ${findConnectionRecommendation?.userFrom?.lastName}`;
        const userFormType = descRawCon?.split(" as ")?.[1]?.replace(".", "");
        const descOnly = `${descRawCon
          ?.split("as")?.[0]
          ?.replace(userFromName, `<span class="capitalize"><b>${userFromName}</b></span>`)
          .replace(
            findConnectionRecommendation?.contextName || "",
            `<b>${findConnectionRecommendation?.contextName}</b>`
          )} as one of your ${userTypeConvert(userFormType as never, undefined, true)}.`;

        const mentorshipType: Record<string, string[]> = {
          SUPER_MENTOR: ["SUPER_MENTOR", "MENTEE"],
          SPONSOR: ["MENTEE", "SUPER_MENTOR"],
          MENTOR: ["MENTOR", "MENTEE"],
          MENTEE: ["MENTEE", "MENTOR"],
          ALLY: ["ALLY", "ALLY"],
        };

        const fromToMentorship =
          mentorshipType[getTargetType(descRawCon as string)];

        return {
          context: "connectionRecommendations",
          createdAt: findConnectionRecommendation?.createdAt || "",
          description: findConnectionRecommendation?.isFromHR
            ? descOnly?.replace("HR Department", "Admin")
            : descOnly,
          contextProfile: {
            id: findConnectionRecommendation?.userFrom?.id || "",
            name: findConnectionRecommendation?.isFromHR ? "Admin" :
              findConnectionRecommendation?.userFrom?.firstName ||
              "Unknown User",
            avatarUrl: findConnectionRecommendation?.userFrom?.avatarUrl || "",
            cgPercent: 0, // ambil dr store anjenk
            points: findConnectionRecommendation?.userFrom?.points || 1,
            isHR: findConnectionRecommendation?.isFromHR || false,
            type: "USER",
          },
          contextReference: {
            id: findConnectionRecommendation?.contextId || "",
            name: findConnectionRecommendation?.contextName || "Unknown User",
            avatarUrl: findConnectionRecommendation?.contextImage || "",
            cgPercent: findConnectionRecommendation?.contextCGPercent || 0,
            type: "USER",
            lookingForType: `${capitalize(detectType(findConnectionRecommendation?.userFrom?.type || ""))}, Actively Looking for ${capitalize(pluralize(detectType(findConnectionRecommendation?.userFrom?.lookingForType || "Connections"), 2))}`,
            jobTitle: findConnectionRecommendation?.userFrom?.jobTitle,
            points: findConnectionRecommendation?.userFrom?.points || 1,
          },
          notes: findConnectionRecommendation?.notifMessage || "",
          actionPayload: {
            recommendToUser: {
              targetUser: findConnectionRecommendation?.contextId,
              type: "USER",
              typeID: findConnectionRecommendation?.notifId,
            },
            requestConnection: {
              userId: findConnectionRecommendation?.contextId,
              message: `${findConnectionRecommendation?.userFrom?.firstName} ${
                findConnectionRecommendation?.userFrom?.lastName
              } sent a connection request as ${userTypeConvert(
                findConnectionRecommendation?.userFrom?.type as any
              )}`,
              userFromType: fromToMentorship[1],
              userToType: fromToMentorship[0],
            },
            misc: {
              name: findConnectionRecommendation?.contextName,
            },
          },
        };

      case "CONNECTION_ACTIVITIES":
        const findConnectionActivity =
          cloneRawActivitiesData.connectionActivities.find(
            (e) => e.notifId === activity.id
          ) || null;

        const connectionActivityDesc = findConnectionActivity?.description === "Congratulations! You are now connected with " ? `Congratulations! You are now connected with <span class="capitalize"><b>${findConnectionActivity?.userFromName?.trimEnd()}</b></span>` :
          findConnectionActivity?.description?.includes(" fan ")
            ? `<b>You</b> are now connected with <span class="capitalize"><b>${findConnectionActivity?.description?.split("has now")[0]}</b></span>`
            : findConnectionActivity?.description?.includes(" champions.")
              ? `<b>You</b> are now one of <b><span class="capitalize">${findConnectionActivity?.userFromName?.trimEnd()}'s</span></b> champions.`
              : findConnectionActivity?.description?.includes(" as a Mentor")
                ? `Congratulations! <b>You</b> are now connected with <b><span class="capitalize">${findConnectionActivity?.userFromName?.trimEnd()}</span></b> as a <b>Mentor</b>.`
                : findConnectionActivity?.description?.includes(" as a Mentee")
                  ? `Congratulations! <b>You</b> are now connected with <b><span class="capitalize">${findConnectionActivity?.userFromName?.trimEnd()}</span></b> as a <b>Mentee</b>.`
                  : findConnectionActivity?.description?.includes(
                        " as a Sponsoree"
                      )
                    ? `Congratulations! <b>You</b> are now connected with <b><span class="capitalize">${findConnectionActivity?.userFromName?.trimEnd()}</span></b> as a <b>Sponsoree</b>.`
                    : findConnectionActivity?.description?.includes(
                          " wants to connect with you."
                        )
                      ? `<span class="capitalize"><b>${findConnectionActivity?.userFromName?.trimEnd()}</b></span> wants to connect with you. <br/>Accept connection request from <span class="capitalize"><b>${findConnectionActivity?.userFromName?.trimEnd()}</b></span>.`
                      : detectType(
                          findConnectionActivity?.description as string
                        ) || "";

        const isConnectionRequest =
          findConnectionActivity?.description?.includes(
            " wants to connect with you."
          ) || false;
        return {
          context: "connectionActivities",
          createdAt: findConnectionActivity?.createdAt || "",
          description: connectionActivityDesc,
          contextProfile: {
            id: findConnectionActivity?.userFromId || "",
            name: `${findConnectionActivity?.userFromName || ""}` || "",
            avatarUrl: findConnectionActivity?.userFromAvatar || "",
            cgPercent: 0, // ambil dr store anjenk
            points: findConnectionActivity?.userFromPoint || 0,
            isHR: findConnectionActivity?.isFromHR || false,
            type: "USER",
          },
          notes: findConnectionActivity?.message || "",
          actionPayload: {
            userFromName: findConnectionActivity?.userFromName,
            id: findConnectionActivity?.userFromId,
          },
          additionalInfo: {
            isConnectionRequest,
          },
        };

      case "RELATION_UPGRADE_REQUESTS":
        const findRelationUpgradeRequest =
          cloneRawActivitiesData.relationshipUpgradeRequests.find(
            (e) => e.notifId === activity.id
          ) || null;

        return {
          context: "relationshipUpgradeRequests",
          contextProfile: {
            avatarUrl: findRelationUpgradeRequest?.userFromAvatar || "",
            id: findRelationUpgradeRequest?.userFromId || "",
            name: findRelationUpgradeRequest?.userFromName || "",
            type: "USER",
            cgPercent: 0, // ambil dr store anjenk
            points: findRelationUpgradeRequest?.userFromPoint || 0,
            isHR: findRelationUpgradeRequest?.isFromHR || false,
          },
          createdAt: activity.createdAt || "",
          description: detectType(
            findRelationUpgradeRequest?.description || ""
          ),
          notes: findRelationUpgradeRequest?.message || "",
          actionPayload: {
            referenceID: findRelationUpgradeRequest?.referenceID,
            name: `You are now connected with ${findRelationUpgradeRequest?.description
              ?.split(" ")
              ?.slice(
                2,
                findRelationUpgradeRequest?.description
                  ?.split(" ")
                  ?.findIndex((x: string) => x === "would")
              )
              ?.join(
                " "
              )} as ${detectType(findRelationUpgradeRequest?.description?.split(" ")?.slice(-1, findRelationUpgradeRequest?.description?.split(" ")?.length)?.join("")?.replace(".", "") as never)}.`,
          },
        };

      case "SKILL_RECOMMENDATIONS":
        const findSkillRecommendation =
          cloneRawActivitiesData.skillRecommendations.find(
            (e) => e.notificationId === activity.id
          ) || null;
        const descriptionSkillRecommendations = `<b>${findSkillRecommendation?.recommender?.user?.firstName || findSkillRecommendation?.recommender?.user?.lastName}</b> recommended a ${findSkillRecommendation?.verifier ? `specific skill that can verified by <b>${findSkillRecommendation?.verifier?.firstName} ${findSkillRecommendation?.verifier?.lastName}</b>` : findSkillRecommendation?.skill ? "Specific Skill" : "Skill Ladder"}.`;
        return {
          context: "skillRecommendations",
          createdAt: findSkillRecommendation?.notification?.createdAt || "",
          description: descriptionSkillRecommendations,
          contextProfile: {
            id: findSkillRecommendation?.recommender?.userId || "",
            name:
              findSkillRecommendation?.recommender?.user?.firstName ||
              findSkillRecommendation?.recommender?.user?.lastName ||
              "Unknown User",
            avatarUrl:
              findSkillRecommendation?.recommender?.user?.avatarUrl || "",
            cgPercent: findSkillRecommendation?.recommender?.CGPercent || 0, // ambil dr store anjenk
            points: findSkillRecommendation?.recommender?.user?.points || 1,
            isHR: false,
            type: "USER",
          },
          contextReference: {
            id: findSkillRecommendation?.skill?.id || "",
            name:
              findSkillRecommendation?.skill?.name ||
              findSkillRecommendation?.skillTrack?.name ||
              "Unknown Skill",
            type: findSkillRecommendation?.skill ? "SKILL" : "SKILL_TRACK",
            avatarUrl: "",
            color:
              findSkillRecommendation?.skill?.skillTrack?.color ||
              findSkillRecommendation?.skillTrack?.color ||
              "",
            department:
              findSkillRecommendation?.skill?.department?.name ||
              findSkillRecommendation?.skillTrack?.department?.name ||
              "",
            skillName:
              findSkillRecommendation?.skill?.skillTrack?.name ||
              findSkillRecommendation?.skillTrack?.name ||
              "",
            skillLevel:
              findSkillRecommendation?.skill?.departmentlevel?.name || "",
          },
          color: findSkillRecommendation?.skillTrack?.color || "",
          actionPayload: {
            targetUser: findSkillRecommendation?.notification?.userTo,
            type: findSkillRecommendation?.skill ? "SKILL" : "SKILL_TRACK",
            typeID: findSkillRecommendation?.notification?.id,
          },
        };

      case "SKILL_VERIFICATION_REQUESTS":
        const findSkillVerificationRequest =
          cloneRawActivitiesData.skillVerificationRequests.find(
            (e) => e.id === activity.id
          ) || null;

        const descriptionSkillVerificationRequests =
          findSkillVerificationRequest?.description?.includes(
            " wants you to verify their skill in "
          )
            ? `<b><span class="capitalize font-bold">${findSkillVerificationRequest?.userFromName}</span></b> wants you to verify their skill in <b>${findSkillVerificationRequest?.completeSkillName}</b> under the <b>${findSkillVerificationRequest?.skillDepartmentName}</b> department.`
            : findSkillVerificationRequest?.description || "";
        return {
          context: "skillVerificationRequests",
          createdAt: findSkillVerificationRequest?.createdAt || "",
          description: descriptionSkillVerificationRequests,
          contextProfile: {
            id: findSkillVerificationRequest?.userFrom || "",
            name: findSkillVerificationRequest?.userFromName || "Unknown User",
            avatarUrl: findSkillVerificationRequest?.userFromAvatarUrl || "",
            cgPercent: 0, // ambil dr store anjenk
            points: findSkillVerificationRequest?.userFromPoint || 0,
            isHR: findSkillVerificationRequest?.isFromHR || false,
            type: "USER",
          },
          contextReference: {
            id: findSkillVerificationRequest?.referenceID || "",
            name:
              findSkillVerificationRequest?.completeSkillName ||
              "Unknown Skill",
            type: "SKILL",
            avatarUrl: "",
            color: findSkillVerificationRequest?.color || "",
          },
          actionPayload: {
            ...findSkillVerificationRequest,
            targetUserID: findSkillVerificationRequest?.userTo,
            targetObjectID: findSkillVerificationRequest?.id,
            rateValue: 0,
            purpose: "DECLINED",
          },
        };

      case "ENDORSEMENTS":
        const findEndorsement =
          cloneRawActivitiesData.activeEndorsements?.find(
            (e) => e.id === activity.id
          ) || cloneRawActivitiesData.endorsements?.find(
            (e) => e.id === activity.id
          ) || null;
        return {
          context: !findEndorsement?.status ? "empty" : findEndorsement?.status === "APPROVED" ? "approvedEndorsement" : "endorsements",
          createdAt: findEndorsement?.createdAt || "",
          description: findEndorsement?.status === "APPROVED" ? `You have accepted ${findEndorsement?.createdBy?.firstName} ${findEndorsement?.createdBy?.lastName} endorsement.` : `<b>${findEndorsement?.createdBy?.firstName || findEndorsement?.createdBy?.lastName}</b> wrote you an endorsement.`,
          contextProfile: {
            id: findEndorsement?.createdBy?.id || "",
            name:
              `${findEndorsement?.createdBy?.firstName || ""} ${findEndorsement?.createdBy?.lastName || ""}` ||
              "Unknown User",
            avatarUrl: findEndorsement?.createdBy?.avatarUrl || "",
            cgPercent: 0, // ambil dr store anjenk
            points: findEndorsement?.createdBy?.points || 1,
            isHR: false,
            type: "USER",
          },
          notes: findEndorsement?.description || "",
          actionPayload: {
            id: findEndorsement?.id,
            _version: findEndorsement?._version,
            name: `${findEndorsement?.createdBy?.firstName || ""} ${findEndorsement?.createdBy?.lastName || ""}`,
          },
        };

      case "CHAMPION_REQUESTS":
        const findChampionRequest =
          cloneRawActivitiesData.championRequests.find(
            (e) => e.id === activity.id
          ) || null;
        const descriptionChampionRequests = `<span class="capitalize"><b>${capitalize(findChampionRequest?.userFrom?.firstName) || capitalize(findChampionRequest?.userFrom?.lastName)}</b></span> would like to honor you as one of <b>${capitalize(findChampionRequest?.userFrom?.firstName) || capitalize(findChampionRequest?.userFrom?.lastName)}'s</b> champions. Would you like to accept this honor and the reward points?`;
        return {
          context: "championRequests",
          createdAt: findChampionRequest?.createdAt || "",
          description: descriptionChampionRequests,
          contextProfile: {
            id: findChampionRequest?.userFrom?.id || "",
            name:
              `${findChampionRequest?.userFrom?.firstName || ""} ${findChampionRequest?.userFrom?.lastName || ""}` ||
              "Unknown User",
            avatarUrl: findChampionRequest?.userFrom?.avatarUrl || "",
            cgPercent: 0, // ambil dr store anjenk
            points: findChampionRequest?.userFrom?.points || 1,
            isHR: false,
            type: "USER",
          },
          notes: findChampionRequest?.requestMessage || "",
          actionPayload: {
            championReqId: findChampionRequest?.id,
          }
        };

      case "MENTORSHIP_REQUESTS":
        const findMentorshipRequest =
          cloneRawActivitiesData.mentorshipRequests.find(
            (e) => e.id === activity.id
          ) || null;

        const askForConnection =
          userTypeConvert(
            findMentorshipRequest?.userFromType as never,
            findMentorshipRequest?.userToType as never,
            true
          ) === "Connections";
        const fromName =
          findMentorshipRequest?.userFrom?.firstName ||
          findMentorshipRequest?.userFrom?.lastName;

        return {
          context: "mentorshipRequests",
          createdAt: findMentorshipRequest?.createdAt || "",
          description: askForConnection
            ? `<b>${fromName?.trimEnd()}</b> wants to connect with you. <br/>Accept connection request from <b>${fromName?.trimEnd()}</b>.`
            : `<b>${findMentorshipRequest?.userFrom?.firstName || findMentorshipRequest?.userFrom?.lastName}</b>
                                    would like you to become one of
                                    <b>
                                      ${fromName || findMentorshipRequest?.userFrom?.lastName}'s
                                    </b>
                                    <b>
                                      ${userTypeConvert(findMentorshipRequest?.userFromType as never, findMentorshipRequest?.userToType as never, true)}</b>.`,
          contextProfile: {
            id: findMentorshipRequest?.userFrom?.id || "",
            name:
              `${findMentorshipRequest?.userFrom?.firstName || ""} ${findMentorshipRequest?.userFrom?.lastName || ""}` ||
              "Unknown User",
            avatarUrl: findMentorshipRequest?.userFrom?.avatarUrl || "",
            cgPercent: 0, // ambil dr store anjenk
            points: findMentorshipRequest?.userFrom?.points || 1,
            isHR: false,
            type: "USER",
          },
          actionPayload: {
            id: findMentorshipRequest?.id,
            _version: findMentorshipRequest?._version,
            fromType: findMentorshipRequest?.userFromType,
            toType: findMentorshipRequest?.userToType,
            name: `${findMentorshipRequest?.userFrom?.firstName || ""} ${findMentorshipRequest?.userFrom?.lastName || ""}`,
          },
          additionalInfo: {
            isConnectionRequest: askForConnection,
          },
        };

      default:
        return {
          context: "empty",
          contextProfile: {
            avatarUrl: "",
            id: "empty",
            name: "",
            type: "USER",
          },
          createdAt: "",
          description: "",
        };
    }
  });
}

async function getAllCGPActivity(
  processedAct: processedActivities[]
): Promise<void> {
  const allUniqueUserIds = processedAct
    ?.filter((x) => x?.contextProfile?.type === "USER")
    .map((x) => x?.contextProfile?.id)
    .filter((value, index, self) => self.indexOf(value) === index && value);

  const allUniqueContextRefUserIds = processedAct
    ?.filter((x) => x?.contextReference?.type === "USER")
    .map((x) => x?.contextReference?.id)
    .filter((value, index, self) => self.indexOf(value) === index && value);

  const allUniqueCircleIds = processedAct
    ?.filter((x) => x?.contextProfile?.type === "CIRCLE")
    .map((x) => x?.contextProfile?.id)
    .filter((value, index, self) => self.indexOf(value) === index && value);

  const allUniqueContextRefCircleIds = processedAct
    ?.filter((x) => x?.contextReference?.type === "CIRCLE")
    .map((x) => x?.contextReference?.id)
    .filter((value, index, self) => self.indexOf(value) === index && value);

  const allUniqueEventIds = processedAct
    ?.filter((x) => x?.contextProfile?.type === "EVENT")
    .map((x) => x?.contextProfile?.id)
    .filter((value, index, self) => self.indexOf(value) === index && value);

  const allUniqueContextRefEventIds = processedAct
    ?.filter((x) => x?.contextReference?.type === "EVENT")
    .map((x) => x?.contextReference?.id)
    .filter((value, index, self) => self.indexOf(value) === index && value);

  const combinedCGPS = [] as { CGPercent: number; id: string; lastFetch: Date }[];

  if ([...allUniqueUserIds, ...allUniqueContextRefUserIds].length !== 0) {
    const userIds = [...allUniqueUserIds, ...allUniqueContextRefUserIds];
    const response = (await API.graphql(
      graphqlOperation(getCGPercentUsers, { userIds, limit: 1000 })
    )) as {
      data: {
        getCGPercentUsers: CGPResponse[];
      };
    };
    combinedCGPS.push(
      ...response?.data?.getCGPercentUsers?.map((x) => ({
        CGPercent: x?.CGPercent,
        id: x?.userId,
        lastFetch: new Date(),
      }))
    );
  }

  if ([...allUniqueCircleIds, ...allUniqueContextRefCircleIds].length !== 0) {
    const referenceIds = [...allUniqueCircleIds, ...allUniqueContextRefCircleIds];
    const response = (await API.graphql(
      graphqlOperation(getCGPercentContext, {
        referenceIds: referenceIds,
        type: "CIRCLE",
        limit: 1000,
      })
    )) as {
      data: {
        getCGPercentContext: CGPContextResponse[];
      };
    };

    combinedCGPS.push(
      ...response?.data?.getCGPercentContext?.map((x) => ({
        CGPercent: x?.CGPercent,
        id: x?.referenceId,
        lastFetch: new Date(),
      }))
    );
  }

  if ([...allUniqueEventIds, ...allUniqueContextRefEventIds].length !== 0) {
    const referenceIds = [...allUniqueEventIds, ...allUniqueContextRefEventIds];
    const response = (await API.graphql(
      graphqlOperation(getCGPercentContext, {
        referenceIds: referenceIds,
        type: "EVENT",
        limit: 1000,
      })
    )) as {
      data: {
        getCGPercentContext: CGPContextResponse[];
      };
    };

    combinedCGPS.push(
      ...response?.data?.getCGPercentContext?.map((x) => ({
        CGPercent: x?.CGPercent,
        id: x?.referenceId,
        lastFetch: new Date(),
      }))
    );
  }

  useCGPStore.setState({
    activityCGP: combinedCGPS,
  });
}

export const useMyActivityV3 = (config: {
  actionLoaderSet?: (purpose: "ACCEPTED" | "DECLINED" | null) => void;
}) => {
  const [isLoadingFetchMyActivityInitial, isLoadingFetchMyActivityInitialSet] =
    useState(true);

  const { successModal, errorModal } = useShowModalResult();
  const { isAuthenticated } = useAuthStore();

  const router = useRouter();

  const fetchMyActivities = useCallback(async () => {
    if (!isAuthenticated) {
      return;
    }
    try {
      const activitiesData = await fetchMyActivitiesHTTP();
      
      const processedActivities = activitiesProcessor(activitiesData)?.filter(
        (x) => x?.context !== "empty"
      );
      // console.log(activitiesProcessor(activitiesData))

      useMyActivityStore.setState({
        activities: activitiesData,
        processedActivities,
      });

      getAllCGPActivity(processedActivities);
    } catch (error) {
      console.error("fetchMyActivities error", error);
    } finally {
      isLoadingFetchMyActivityInitialSet(false);
    }
  }, [isAuthenticated]);

  const activityActionsMethods: Partial<
    Record<
      Exclude<processedActivities["context"], "empty">,
      (payload: any) => Promise<void>
    >
  > = {
    skillRecommendations: async (payload) => {
      config?.actionLoaderSet?.(payload?.purpose as never);
      await skillRecommendationsUsecase(payload, {
        successModal,
        errorModal,
        refetch: fetchMyActivities,
      });
      config?.actionLoaderSet?.(null);
    },
    relationshipUpgradeRequests: async (payload) => {
      config?.actionLoaderSet?.(payload?.purpose as never);
      await relationshipUpgradeUsecase(payload, {
        successModal,
        errorModal,
        refetch: fetchMyActivities,
      });
      config?.actionLoaderSet?.(null);
    },
    connectionRecommendations: async (payload) => {
      config?.actionLoaderSet?.(payload?.purpose as never);
      await connectionRecommendationsUsecase(payload, {
        successModal,
        errorModal,
        refetch: fetchMyActivities,
      });
      config?.actionLoaderSet?.(null);
    },
    eventInvitations: async (payload) => {
      config?.actionLoaderSet?.(payload?.purpose as never);
      await circleEventInviteUsecase(payload, {
        successModal,
        errorModal,
        refetch: fetchMyActivities,
      });
      config?.actionLoaderSet?.(null);
    },
    circleInvitations: async (payload) => {
      config?.actionLoaderSet?.(payload?.purpose as never);
      await circleEventInviteUsecase(payload, {
        successModal,
        errorModal,
        refetch: fetchMyActivities,
      });
      config?.actionLoaderSet?.(null);
    },
    circleRecommendations: async (payload) => {
      config?.actionLoaderSet?.(payload?.purpose as never);
      await circleEventRecommendationUsecase(payload, {
        successModal,
        errorModal,
        refetch: fetchMyActivities,
      });
      config?.actionLoaderSet?.(null);
    },
    eventRecommendations: async (payload) => {
      config?.actionLoaderSet?.(payload?.purpose as never);
      await circleEventRecommendationUsecase(payload, {
        successModal,
        errorModal,
        refetch: fetchMyActivities,
      });
      config?.actionLoaderSet?.(null);
    },
    mentorshipRequests: async (payload) => {
      config?.actionLoaderSet?.(payload?.purpose as never);
      await mentorshipRequestUsecase(payload, {
        successModal,
        errorModal,
        refetch: fetchMyActivities,
      });
      config?.actionLoaderSet?.(null);
    },
    championRequests: async (payload) => {
      config?.actionLoaderSet?.(payload?.purpose as never);
      await championRequestUsecase(payload, {
        successModal,
        errorModal,
        refetch: fetchMyActivities,
        router,
      });
      config?.actionLoaderSet?.(null);
    },
    skillVerificationRequests: async (payload) => {
      config?.actionLoaderSet?.(payload?.purpose as never);
      await skillVerificationRequestUsecase(payload, {
        successModal,
        errorModal,
        refetch: fetchMyActivities,
      });
      config?.actionLoaderSet?.(null);
    },
    endorsements: async (payload) => {
      config?.actionLoaderSet?.(payload?.purpose as never);
      await endorsementUsecase(payload, {
        successModal,
        errorModal,
        refetch: fetchMyActivities,
      });
      config?.actionLoaderSet?.(null);
    },
    connectionActivities: async (payload) => {
      config?.actionLoaderSet?.(payload?.purpose as never);
      await connectionActivitiesUsecase(payload, {
        successModal,
        errorModal,
        refetch: fetchMyActivities,
      });
      config?.actionLoaderSet?.(null);
    },
  };

  return {
    fetchMyActivities,
    isLoadingFetchMyActivityInitial,
    activityActionsMethods,
  };
};
